import { generatePath } from 'react-router-dom';

import { BILLING_DASHBOARD_PAGE } from '@/billing/constants/pages';
import { ClientSetting } from '@/common/constants/clientSettings';
import {
  CATALOG_DIAMONDS_COLORLESS_EDIT_PAGE,
  CATALOG_DIAMONDS_COLORLESS_PAGE,
  CATALOG_DIAMONDS_FANCY_EDIT_PAGE,
  CATALOG_DIAMONDS_FANCY_PAGE,
  CATALOG_GEMS_EDIT_PAGE,
  CATALOG_GEMS_PAGE,
  CATALOG_JEWELRY_EDIT_PAGE,
  CATALOG_JEWELRY_PAGE,
  CATALOG_LGD_COLORLESS_EDIT_PAGE,
  CATALOG_LGD_COLORLESS_PAGE,
  CATALOG_LGD_FANCY_EDIT_PAGE,
  CATALOG_LGD_ROUGH_EDIT_PAGE,
  CATALOG_LGD_ROUGH_PAGE,
  CATALOG_ROUGH_EDIT_PAGE,
  CATALOG_ROUGH_PAGE,
  COLLECTIONS_PAGE,
  CONTACTS_PAGE,
  MULTI_PROJECTS_PAGE,
  PROJECTS_PAGE,
  SHARED_MULTI_PROJECTS_PAGE,
  SHARED_WITH_ME_PROJECTS_PAGE,
  TASKS_PAGE,
  WIDGET_CONSTRUCTOR_PAGE,
} from '@/common/constants/pages';
import {
  CAN_ACCESS_ADMIN_PAGE,
  CAN_EDIT_MULTISTAGE,
  LGD,
  MANAGE_CONTACTS,
  MANAGER,
  securityManager,
  VIEW_STOCK,
} from '@/common/services/SecurityManager';
import URLProvider from '@/common/services/URLProvider';
import { SERVICES_PAGE } from '@/conversion/constants/pages';
import { DRIVE_ROOT_NAME } from '@/drive/constants/driveRootName';
import AppSectionTreeItem from '@/drive/entities/DriveTreeItem/AppSectionTreeItem';
import { DriveTreeItem } from '@/drive/entities/DriveTreeItem/DriveTreeItem';
import { DriveSection } from '@/drive/types/DriveSection';
import { SectionItem } from '@/drive/types/SectionItem';
import { User } from '@/user/entity/User';

const driveRootSection = {
  title: DRIVE_ROOT_NAME,
  id: DriveSection.DriveRoot,
  collapsed: false,
  icon: 'cutwise-drive',
} as const;

export function createDriveRootSection() {
  return new AppSectionTreeItem(driveRootSection);
}

export function createSidebarTreeSections(user: User): DriveTreeItem[] {
  const sections: SectionItem[] = [
    {
      title: 'Public Cutwise Catalogue',
      icon: 'cutwise',
      id: DriveSection.Public,
      children: [
        {
          title: 'Natural Diamonds',
          id: DriveSection.PublicDiamonds,
          children: [
            {
              title: 'Polished Colorless',
              icon: 'diamond',
              id: DriveSection.PublicColorless,
              url: CATALOG_DIAMONDS_COLORLESS_PAGE,
            },
            {
              title: 'Polished Fancy Color',
              icon: 'diamond',
              iconClassName: 'accent',
              id: DriveSection.PublicFancy,
              url: CATALOG_DIAMONDS_FANCY_PAGE,
            },
            {
              title: 'Rough',
              icon: 'rough',
              id: DriveSection.PublicRough,
              url: CATALOG_ROUGH_PAGE,
            },
            {
              title: 'Gems',
              icon: 'gem',
              id: DriveSection.PublicGems,
              url: CATALOG_GEMS_PAGE,
            },
          ],
        },
        {
          title: 'Lab-Grown Diamonds',
          id: DriveSection.PublicLgd,
          children: [
            {
              title: 'Polished Colorless',
              icon: 'diamond',
              id: DriveSection.PublicLgdColorless,
              url: CATALOG_LGD_COLORLESS_PAGE,
            },
            {
              title: 'Polished Fancy Color',
              icon: 'diamond',
              iconClassName: 'accent',
              id: DriveSection.PublicLgdFancy,
              url: CATALOG_DIAMONDS_FANCY_PAGE,
            },
            {
              title: 'Rough',
              icon: 'rough',
              id: DriveSection.PublicLgdRough,
              url: CATALOG_LGD_ROUGH_PAGE,
            },
          ],
        },
        {
          title: 'Jewelry',
          icon: 'jewelry',
          id: DriveSection.PublicJewelry,
          url: CATALOG_JEWELRY_PAGE,
        },
        { title: 'Demo', id: DriveSection.DemoCollections, url: COLLECTIONS_PAGE },
      ],
    },
    {
      id: DriveSection.PinnedItems,
      title: '',
      icon: 'pin-filled',
      isSectionEndAbove: true,
    },
    {
      title: 'Light-Spectrum Demo',
      id: DriveSection.Spectrum,
      icon: 'metrics-color-material',
    },
    {
      title: 'Shared with Me',
      id: DriveSection.SharedWithMe,
      icon: 'recent-shared-with-me',
    },
    driveRootSection,
  ];

  sections.push(createPrivateRoot(user));

  return sections.map((section) => new AppSectionTreeItem(section));
}

function createPrivateRoot(user: User): SectionItem {
  const privateRoot: SectionItem = {
    title: '',
    id: DriveSection.PrivateRoot,
    isSectionEndAbove: true,
    children: [
      {
        title: 'My Profile',
        id: DriveSection.Profile,
        icon: 'user-profile',
        url: '/profile',
      },
    ],
  };

  if (!user?.hasSetting(ClientSetting.CustomBilling)) {
    privateRoot.children!.push({
      title: 'Billing Dashboard',
      id: DriveSection.Billing,
      url: BILLING_DASHBOARD_PAGE,
      icon: 'user-billing-eur',
    });
  }

  const multistageProjectsSection: SectionItem = {
    title: 'Multistage Projects',
    isSubSection: true,
    id: DriveSection.MultistageProjects,
    children: [],
  };

  if (securityManager.isGranted(CAN_EDIT_MULTISTAGE)) {
    multistageProjectsSection.children!.push({
      title: 'Owned by Me',
      id: DriveSection.Multistage,
      url: MULTI_PROJECTS_PAGE,
      icon: 'multistage',
    });
  }

  multistageProjectsSection.children!.push({
    title: 'Shared With Me',
    id: DriveSection.SharedMultistage,
    url: SHARED_MULTI_PROJECTS_PAGE,
    icon: 'multistage-shared',
  });

  privateRoot.children!.push(multistageProjectsSection);

  const projectsSection: SectionItem = {
    title: 'Projects',
    isSubSection: true,
    id: DriveSection.PrivateProjects,
    children: [],
  };

  if (securityManager.isGranted(VIEW_STOCK)) {
    projectsSection.children!.push({
      title: 'Owned by Me',
      id: DriveSection.MyProjects,
      url: PROJECTS_PAGE,
      icon: 'project',
    });
  }

  projectsSection.children!.push({
    title: 'Shared with Me',
    id: DriveSection.SharedProjects,
    url: SHARED_WITH_ME_PROJECTS_PAGE,
    icon: 'project-shared',
  });

  privateRoot.children!.push(projectsSection);

  if (securityManager.isGranted(VIEW_STOCK) && !securityManager.isGranted(LGD)) {
    privateRoot.children!.push({
      title: 'My Natural Diamonds',
      id: DriveSection.PrivateDiamonds,
      isSubSection: true,
      children: [
        {
          title: 'Colorless',
          icon: 'diamond',
          id: DriveSection.PrivateColorless,
          url: CATALOG_DIAMONDS_COLORLESS_EDIT_PAGE,
        },
        {
          title: 'Fancy Color',
          icon: 'diamond',
          id: DriveSection.PrivateFancy,
          url: CATALOG_DIAMONDS_FANCY_EDIT_PAGE,
          iconClassName: 'accent',
        },
        {
          title: 'Rough',
          icon: 'rough',
          id: DriveSection.PrivateRough,
          url: CATALOG_ROUGH_EDIT_PAGE,
        },
      ],
    });

    privateRoot.children!.push({
      title: 'Other',
      id: DriveSection.Other,
      isSubSection: true,
      children: [
        {
          title: 'Jewelry',
          icon: 'jewelry',
          id: DriveSection.PrivateJewelry,
          url: CATALOG_JEWELRY_EDIT_PAGE,
        },
        {
          title: 'Gems',
          icon: 'gem',
          id: DriveSection.PrivateGems,
          url: CATALOG_GEMS_EDIT_PAGE,
        },
      ],
    });
  }

  if (securityManager.isGranted(VIEW_STOCK) && securityManager.isGranted(LGD)) {
    privateRoot.children!.push({
      title: 'Lab-grown diamonds',
      isSubSection: true,
      id: DriveSection.PrivateLgd,
      children: [
        {
          title: 'Colorless',
          icon: 'diamond',
          id: DriveSection.PrivateLgdColorless,
          url: CATALOG_LGD_COLORLESS_EDIT_PAGE,
        },
        {
          title: 'Fancy Color',
          id: DriveSection.PrivateLgdFancy,
          url: CATALOG_LGD_FANCY_EDIT_PAGE,
          icon: 'diamond',
          iconClassName: 'accent',
        },
        {
          title: 'Rough',
          icon: 'rough',
          id: DriveSection.PrivateLgdRough,
          url: CATALOG_LGD_ROUGH_EDIT_PAGE,
        },
      ],
    });
  }

  const servicesSection: SectionItem = {
    title: 'Services',
    id: DriveSection.ServicesGroup,
    isSubSection: true,
    children: [],
  };

  servicesSection.children!.push(
    {
      title: 'Services',
      id: DriveSection.Services,
      url: SERVICES_PAGE,
      icon: 'file-conversion',
    },
    {
      title: 'My Tasks',
      url: TASKS_PAGE,
      id: DriveSection.Tasks,
      icon: 'pending',
    },
  );

  if (securityManager.isGranted(MANAGER)) {
    servicesSection.children!.push({
      title: 'Widget Builder',
      id: DriveSection.WidgetBuilder,
      url: generatePath(WIDGET_CONSTRUCTOR_PAGE),
      icon: 'widgets',
    });
  }

  if (securityManager.isGranted(CAN_ACCESS_ADMIN_PAGE)) {
    servicesSection.children!.push({
      title: 'Admin',
      id: DriveSection.Admin,
      url: URLProvider.adminPage(),
      icon: 'full-protect',
      isExternalUrl: true,
    });
  }

  if (securityManager.isGranted(MANAGE_CONTACTS)) {
    servicesSection.children!.push({
      title: 'Contacts',
      id: DriveSection.Contacts,
      url: generatePath(CONTACTS_PAGE),
      icon: 'private-access-for-users',
    });
  }

  privateRoot.children!.push(servicesSection);

  return privateRoot;
}
